<template>
  <div class="osd">
    <div class="osd-header">
      <div class="osd-left-title">{{ orderDashboardTitle || "Order Dashboard" }}</div>
      <div class="eyebrow">
        <h6>Order Status — <span class="ts">{{ formattedLatestTs }}</span></h6>
      </div>
    </div>
    

    <div v-if="kpis.length" class="kpi-row">
      <div class="kpi" v-for="k in kpis" :key="k.label">
        <div class="kpi-inline">
          <div class="label">{{ k.label }}</div>
          <div class="value">{{ formatKpiValue(k) }}</div>
          <svg class="spark" viewBox="0 0 100 32" preserveAspectRatio="none">
            <path :d="sparkPath(k.trend)" fill="none" stroke="#0f8a7d" stroke-width="1.6" opacity="0.85" />
          </svg>
        </div>
      </div>
    </div>

    <div class="table-wrap">
      <table>
        <thead>
        <tr>
          <th
            v-for="column in tableColumns"
            :key="column.key"
            @click="sortBy(column.key)"
            :class="{ sorted: sortKey === column.key, num: isNumericColumn(column) }"
          >
            <span class="th-content">
              <span>{{ column.label }}</span>
              <span class="sort-icon" :class="{ active: sortKey === column.key }">{{ getSortIcon(column.key) }}</span>
            </span>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(row, index) in sortedOrders" :key="index">
          <td
            v-for="column in tableColumns"
            :key="column.key"
            :class="cellClass(column, row[column.key])"
          >
            {{ formatCellValue(column, row[column.key]) }}
          </td>
        </tr>
        <tr v-if="!sortedOrders.length">
          <td :colspan="Math.max(tableColumns.length, 1)" class="empty-state">No order data</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
  
  <script>
  import * as d3 from "d3";
  import { inrFormatterV2 } from "@/common/functions";
  
  export default {
    name: "OrderStatusDashboard",
    props: {
      orders: {
        type: Array,
        default: () => []
      },
      kpiData: {
        type: [Array, Object],
        default: () => []
      },
      kpiConfig: {
        type: Array,
        default: () => []
      },
      reportHeaderMap: {
        type: Object,
        default: () => ({})
      },
      useOrderedHeaderSuffix: {
        type: Boolean,
        default: false
      },
      orderDashboardTitle: {
        type: String,
        default: ""
      }
    },
    data() {
      return {
        sortKey: null,
        sortAsc: true
      };
    },
    computed: {
      tableColumns() {
        const normalizeLabel = (rawLabel) => {
          const labelText = String(rawLabel || "");
          if (!this.useOrderedHeaderSuffix) {
            return {
              label: labelText.trim(),
              order: Number.MAX_SAFE_INTEGER
            };
          }
          const match = labelText.match(/^(.*?)-(\d+)$/);
          if (!match) {
            return {
              label: labelText.trim(),
              order: Number.MAX_SAFE_INTEGER
            };
          }
          return {
            label: match[1].trim(),
            order: Number(match[2])
          };
        };

        return Object.entries(this.reportHeaderMap || {})
          .map(([rawLabel, key], idx) => {
            const normalized = normalizeLabel(rawLabel);
            return {
              label: normalized.label,
              key,
              order: normalized.order,
              idx
            };
          })
          .sort((a, b) => {
            if (a.order === b.order) return a.idx - b.idx;
            return a.order - b.order;
          });
      },
      normalizedRows() {
        return Array.isArray(this.orders) ? this.orders : [];
      },
      kpiSource() {
        if (Array.isArray(this.kpiData)) {
          return this.kpiData[0] || {};
        }
        return this.kpiData || {};
      },
      timestampKey() {
        if (!this.tableColumns.length) return null;
        const matching = this.tableColumns.find(col => /date|time|timestamp/i.test(col.key) || /date|time|timestamp/i.test(col.label));
        return matching ? matching.key : null;
      },
      latestTs() {
        if (!this.timestampKey || !this.normalizedRows.length) return null;
        const parsedDates = this.normalizedRows
          .map(row => this.parseDateValue(row[this.timestampKey]))
          .filter(Boolean);
        if (!parsedDates.length) return null;
        return parsedDates.reduce((max, d) => (d > max ? d : max));
      },
      formattedLatestTs() {
        return this.latestTs ? this.formatTs(this.latestTs) : "—";
      },
      kpis() {
        return (this.kpiConfig || []).map(kpi => {
          const valueKey = kpi.value;
          const value = valueKey ? this.kpiSource[valueKey] : null;
          const numericValue = Number(value);
          const trendBase = Number.isFinite(numericValue) ? numericValue : 1;
          return {
            label: kpi.label || valueKey || "KPI",
            value,
            money: Boolean(kpi.money || kpi.currency || /amount|amt|revenue|value/i.test(kpi.label || "")),
            trend: [trendBase * 0.6, trendBase * 0.75, trendBase * 0.68, trendBase * 0.85, trendBase * 0.8, trendBase * 0.92, trendBase]
          };
        });
      },
      sortedOrders() {
        if (!this.sortKey) return this.normalizedRows;
        const key = this.sortKey;
        const dir = this.sortAsc ? 1 : -1;
        return [...this.normalizedRows].sort((a, b) => {
          const av = a[key];
          const bv = b[key];
          const ad = this.parseDateValue(av);
          const bd = this.parseDateValue(bv);
          if (ad && bd) return (ad - bd) * dir;
          const an = Number(av);
          const bn = Number(bv);
          if (Number.isFinite(an) && Number.isFinite(bn)) return (an - bn) * dir;
          const as = String(av ?? "").toLowerCase();
          const bs = String(bv ?? "").toLowerCase();
          return (as > bs ? 1 : as < bs ? -1 : 0) * dir;
        });
      }
    },
    methods: {
      sortBy(key) {
        if (this.sortKey !== key) {
          // 1st click on a column -> ascending
          this.sortKey = key;
          this.sortAsc = true;
          return;
        }

        if (this.sortAsc) {
          // 2nd click -> descending
          this.sortAsc = false;
          return;
        }

        // 3rd click -> reset to original order
        this.sortKey = null;
        this.sortAsc = true;
      },
      getSortIcon(key) {
        if (this.sortKey !== key) return "↕";
        return this.sortAsc ? "↑" : "↓";
      },
      isNumericColumn(column) {
        return /metric|count|qty|quantity|amount|amt|total|value|units|price|sales/i.test(column.key) ||
          /count|qty|quantity|amount|amt|total|value|units|price|sales/i.test(column.label);
      },
      isDateColumn(column) {
        return /date|time|timestamp/i.test(column.key) || /date|time|timestamp/i.test(column.label);
      },
      isMoneyColumn(column) {
        return /amount|amt|revenue|price|value/i.test(column.key) || /amount|amt|revenue|price|value/i.test(column.label);
      },
      isMetricColumn(column) {
        const key = String(column?.key || "").toLowerCase();
        return key.startsWith("metric");
      },
      cellClass(column, value) {
        return {
          num: this.isNumericColumn(column),
          time: this.isDateColumn(column),
          amt: this.isMoneyColumn(column) && this.isNumericValue(value)
        };
      },
      isNumericValue(value) {
        return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
      },
      parseDateValue(value) {
        if (!value && value !== 0) return null;
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      },
      formatCellValue(column, value) {
        if (value === null || value === undefined || value === "") return "—";
        if (this.isDateColumn(column)) {
          const parsed = this.parseDateValue(value);
          return parsed ? this.formatTs(parsed) : value;
        }
        if (this.isMetricColumn(column) && this.isNumericValue(value)) {
          if (this.isMoneyColumn(column)) return this.formatMoney(Number(value));
          return this.formatNum(Number(value));
        }
        return value;
      },
      formatKpiValue(kpi) {
        if (kpi.value === null || kpi.value === undefined || kpi.value === "") return "—";
        if (this.isNumericValue(kpi.value)) {
          return kpi.money ? this.formatMoney(Number(kpi.value)) : this.formatNum(Number(kpi.value));
        }
        return kpi.value;
      },
      formatTs(d) {
        return d3.timeFormat("%Y-%m-%d %H:%M:%S")(d);
      },
      formatWithInrFormatter(v) {
        const numericValue = Number(v);
        if (!Number.isFinite(numericValue)) return "NA";
        const menuId = this.$route?.params?.submenuId || "RDemo1";
        return inrFormatterV2(numericValue, menuId);
      },
      formatNum(v) {
        return this.formatWithInrFormatter(v);
      },
      formatMoney(v) {
        return "₹" + this.formatWithInrFormatter(v);
      },
      sparkPath(trend) {
        const w = 100, h = 32;
        const x = d3.scaleLinear().domain([0, trend.length - 1]).range([2, w - 2]);
        const y = d3.scaleLinear().domain(d3.extent(trend)).range([h - 4, 4]);
        const line = d3.line().x((v, i) => x(i)).y(v => y(v)).curve(d3.curveMonotoneX);
        return line(trend);
      }
    }
  };
  </script>
  
  <style scoped>
  .osd {
    --bg: #ffffff;
    --panel: #ffffff;
    --line: #d7dce4;
    --ink: #12161f;
    --sub: #5b6472;
    --accent: #0f8a7d;
    --accent2: #c96a1f;
    --mono: 'JetBrains Mono', 'SFMono-Regular', Consolas, monospace;
    --sans: 'Inter', -apple-system, Segoe UI, sans-serif;
  
    background: var(--bg);
    color: var(--ink);
    font-family: var(--sans);
    padding: 4px 6px;
  }
  .osd-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: end;
  }
  .osd-left-title {
    color: #083DB1;
    font-size: 18px;
    font-weight: 700;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 10px;
  }
  .eyebrow {
    grid-column: 2;
    font-family: var(--mono); font-size: 14px; letter-spacing: .14em; font-weight: 600;
    color: var(--sub); text-transform: uppercase; margin-bottom: 4px; text-align: center;
  }
  .eyebrow h5 { font-size: 20px; margin: 0 0 8px 0; font-weight: 600; }
  .ts { font-family: var(--mono); color: var(--accent); font-size: 13px; }
  
  .kpi-row {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px;
    margin-bottom: 22px;
  }
  .kpi {
    background: var(--panel); border: 1px solid var(--line); border-radius: 6px;
    padding: 14px 16px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(18,22,31,0.04);
  }
  .kpi-inline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  .kpi .label {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--accent2);
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    white-space: nowrap;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .kpi .value {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .kpi .spark {
    display: block;
    width: 74px;
    max-width: 74px;
    height: 24px;
    opacity: .9;
    flex-shrink: 0;
  }
  .kpi .spark path {
    vector-effect: non-scaling-stroke;
  }
  
  .table-wrap {
    width: 100%;
    min-height: 100px;
    max-height: clamp(100px, 30vh, 260px);
    overflow: auto;
    border: 1px solid var(--line);
    border-radius: 6px;
    scrollbar-width: thin;
    scrollbar-color: #aeb8c7 #f4f6fa;
    -webkit-overflow-scrolling: touch;
  }
  .table-wrap::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  .table-wrap::-webkit-scrollbar-track {
    background: #f4f6fa;
    border-radius: 8px;
  }
  .table-wrap::-webkit-scrollbar-thumb {
    background: #aeb8c7;
    border-radius: 8px;
    border: 2px solid #f4f6fa;
  }
  .table-wrap::-webkit-scrollbar-thumb:hover {
    background: #8f9aad;
  }
  table {
    width: max(100%, 640px);
    min-width: 640px;
    border-collapse: collapse;
    background: var(--panel);
    font-variant-numeric: tabular-nums;
  }
  thead th {
    text-align: left; font-family: var(--mono); font-size: 10.5px;
    text-transform: uppercase; letter-spacing: .06em; color: var(--sub);
    padding: 10px 12px; border-bottom: 1px solid var(--line); cursor: pointer;
    user-select: none; white-space: nowrap; background: #f7f8fa;
    position: sticky;
    top: 0;
    z-index: 2;
    font-weight: 700;
    border-right: 1px solid var(--line);
  }
  thead th:last-child { border-right: none; }
  thead th:hover { color: var(--accent); }
  .th-content {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sort-icon {
    color: #95a0b3;
    font-size: 11px;
    line-height: 1;
  }
  .sort-icon.active {
    color: var(--accent2);
    font-weight: 700;
  }
  tbody td {
    padding: 9px 12px;
    border-bottom: 1px solid var(--line);
    border-right: 1px solid var(--line);
    font-size: 13px;
    text-align: left;
  }
  tbody td:last-child { border-right: none; }
  tbody tr:hover { background: #f2f6f5; }
  tbody tr:last-child td { border-bottom: none; }
  .num { text-align: right; font-family: var(--mono); }
  tbody td.num {
    color: steelblue;
    font-weight: 600;
  }
  .cust { color: var(--ink); }
  .time { color: var(--sub); font-family: var(--mono); font-size: 12px; }
  .amt { color: steelblue; font-weight: 600; }
  .empty-state {
    text-align: center;
    color: var(--sub);
    padding: 16px 12px;
  }
  
  @media (max-width: 900px) {
    .kpi-row { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 600px) {
    .osd { padding: 16px 14px; }
    .osd-header {
      grid-template-columns: 1fr;
      row-gap: 4px;
      align-items: start;
    }
    .osd-left-title {
      font-size: 15px;
      grid-column: auto;
    }
    .eyebrow {
      grid-column: auto;
      text-align: left;
    }
    .eyebrow h5 { font-size: 17px; margin-bottom: 4px; }
    .kpi-row { grid-template-columns: 1fr; gap: 8px; margin-bottom: 14px; }
    .kpi { padding: 10px 10px; }
    .kpi-inline {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto 72px;
      align-items: center;
      gap: 8px;
    }
    .kpi .label {
      font-size: 11px;
      letter-spacing: .06em;
      text-align: left;
      width: auto;
    }
    .kpi .value { font-size: 20px; }
    .kpi .spark {
      width: 72px;
      max-width: 72px;
      height: 20px;
      opacity: .92;
    }
    .kpi .spark path { stroke-width: 2px; }
    .table-wrap {
      min-height: 180px;
      max-height: 52vh;
    }
    table { min-width: 520px; }
    thead th, tbody td { padding: 8px 10px; font-size: 12px; }
  }
  @media (max-width: 380px) {
    .kpi-row { grid-template-columns: 1fr; }
  }
  </style>